import { createBrowserRouter, Outlet, useLocation, Navigate } from 'react-router-dom'
import { Suspense, useEffect } from 'react'
import { lazyWithReload as lazy } from './shell/lazyWithReload'
import Sidebar from './shell/Sidebar'
import Header from './shell/Header'
import Aktivitaetsanzeige from './shell/Aktivitaetsanzeige'
import ToastContainer from './shell/ToastContainer'
import RoleGuard from './shell/RoleGuard'
import FeatureFlagGate from './shell/FeatureFlagGate'
import LoginPage from './shell/LoginPage'
import CommandPalette from './shell/CommandPalette'
import { BotWidget } from './modules/bot'
import { GlassScene } from './shared/ui/GlassScene'
import { useAuth } from './auth/AuthProvider'
import { useUIStore } from './store/uiStore'
import { modules as baseModules, NotFoundPage, AccessDeniedPage } from './modules/registry'
import SchulenDashboard from './modules/schulen/SchulenDashboard'
import LandingResolver from './shell/LandingResolver'
import QmWeiche from './shell/QmWeiche'

// ── Kostenstellen module — nested route group ──────────────────────────────────
// Mounted manually so the inner SubNav rail stays mounted across sub-route changes.
const KostenstellenPage         = lazy(() => import('./modules/verwaltung/kostenstellen/KostenstellenPage'))
const KostenstellenUebersicht   = lazy(() => import('./modules/verwaltung/kostenstellen/pages/UebersichtPage'))
const KostenstellenImports      = lazy(() => import('./modules/verwaltung/kostenstellen/pages/ImportsPage'))
const KostenstellenImportWizard = lazy(() => import('./modules/verwaltung/kostenstellen/pages/ImportWizardPage'))
const KostenstellenAssImport    = lazy(() => import('./modules/verwaltung/kostenstellen/pages/AssImportPage'))
const KostenstellenWinfoodImport = lazy(() => import('./modules/verwaltung/kostenstellen/pages/WinfoodImportPage'))
const KostenstellenBrueningAvis = lazy(() => import('./modules/verwaltung/kostenstellen/pages/BrueningAvisPage'))
const KostenstellenNetteImport = lazy(() => import('./modules/verwaltung/kostenstellen/pages/NettePapierImportPage'))
const KostenstellenSapImport     = lazy(() => import('./modules/verwaltung/kostenstellen/pages/SapImportPage'))
const KostenstellenCcPushLog     = lazy(() => import('./modules/verwaltung/kostenstellen/pages/ChefsCulinarPushPage'))
const KostenstellenRkKonten      = lazy(() => import('./modules/verwaltung/kostenstellen/pages/RosenkrantzAccountsPage'))
const KostenstellenRkAbruf       = lazy(() => import('./modules/verwaltung/kostenstellen/pages/RosenkrantzPullPage'))
const KostenstellenRoutingPruef  = lazy(() => import('./modules/verwaltung/kostenstellen/pages/RoutingPruefungPage'))
// 2026-05-26: PCA UI is the single Explorer at /verwaltung/kostenstellen/pca.
// 2026-05-27: DSFinV-K / Kiosk surfaces removed — PCA is now strictly the PCA-DB
// (booking) data path. Old /imports/pca + /imports/pca-bookings still redirect.
const KostenstellenPcaExplorer   = lazy(() => import('./modules/verwaltung/kostenstellen/pages/PcaExplorerPage'))
const KostenstellenSapKasse      = lazy(() => import('./modules/verwaltung/kostenstellen/pages/SapKassePage'))
const KostenstellenDatevImport   = lazy(() => import('./modules/verwaltung/kostenstellen/pages/DatevPayrollImportPage'))
const KostenstellenRefoodImport  = lazy(() => import('./modules/verwaltung/kostenstellen/pages/RefoodImportPage'))
const KostenstellenStammdaten   = lazy(() => import('./modules/verwaltung/kostenstellen/pages/StammdatenPage'))
const KostenstellenPreiskatalog  = lazy(() => import('./modules/verwaltung/kostenstellen/pages/PreiskatalogPage'))
const KostenstellenBioQuote      = lazy(() => import('./modules/verwaltung/kostenstellen/pages/BioQuotePage'))
const KostenstellenKatalogImport = lazy(() => import('./modules/verwaltung/kostenstellen/pages/KatalogImportPage'))
const KostenstellenRefood       = lazy(() => import('./modules/verwaltung/kostenstellen/pages/RefoodPage'))
const KostenstellenAuswertung   = lazy(() => import('./modules/verwaltung/kostenstellen/pages/AuswertungPage'))
const SchoolDeepDive            = lazy(() => import('./modules/verwaltung/kostenstellen/pages/SchoolDeepDivePage'))
const KostenstellenLieferanten  = lazy(() => import('./modules/verwaltung/kostenstellen/pages/LieferantenAuswertungPage'))
const LieferantenKategorisierung = lazy(() => import('./modules/verwaltung/lieferanten/pages/KategorisierungPage'))
const MappingWorkbench           = lazy(() => import('./modules/verwaltung/lieferanten/pages/MappingWorkbenchPage'))
const KostenartenPage            = lazy(() => import('./modules/verwaltung/kostenarten/KostenartenPage'))
const KasseKontrolle             = lazy(() => import('./modules/kasse/KontrollePage'))
const KuechenPage                = lazy(() => import('./modules/schulen/KuechenPage'))
const KpiSettings                = lazy(() => import('./modules/verwaltung/einstellungen/pages/KpiSettingsPage'))

// M26 Mitarbeiterportal — staff-facing shell, mounted as a second top-level
// route below (sibling of path:'/'), not nested under RootLayout. Own auth
// (cookie-based, GET /api/staff/me), own nested <Routes> inside the chunk —
// see staff/StaffApp.tsx and docs/m26-mitarbeiterportal/COUNCIL-REVIEW.md's
// router-architecture note.
const StaffApp = lazy(() => import('./staff/StaffApp'))
// M27 Schulportal — same reasoning as StaffApp: sibling top-level, own auth (school card cookie).
const SchoolApp = lazy(() => import('./school/SchoolApp'))

// HR · Onboarding — dynamic routes (registry lists them with noRoute:true)
const ProbearbeiterFormPage   = lazy(() => import('./modules/hr/onboarding/ProbearbeiterFormPage').then(m => ({ default: m.ProbearbeiterFormPage })))
const ProbearbeiterReviewPage = lazy(() => import('./modules/hr/onboarding/ProbearbeiterReviewPage').then(m => ({ default: m.ProbearbeiterReviewPage })))
const OnboardingWizard        = lazy(() => import('./modules/hr/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })))

// HR · Contracts (dynamic route with :mainId + :vertragId)
const ContractPage            = lazy(() => import('./modules/hr/contracts/ContractPage').then(m => ({ default: m.ContractPage })))

// HACCP · detail routes (registry lists them noRoute:true; mounted manually here,
// each under its OWN module — haccp-geraet/-berichte/-einstellungen/-onboarding —
// so /it/role-admin can switch them separately; seeded from 'haccp' by mig 335).
const HaccpGeraetPage         = lazy(() => import('./modules/haccp/HaccpGeraetPage'))
const HaccpBerichtePage       = lazy(() => import('./modules/haccp/HaccpBerichtePage'))
const HaccpEinstellungenPage  = lazy(() => import('./modules/haccp/HaccpEinstellungenPage'))
const HaccpOnboardingPage     = lazy(() => import('./modules/haccp/HaccpOnboardingPage'))
// M30 QM — Leitung pages (all share module 'qm-leitung'; /qm-leitung lands on the Übersicht and keeps its query, e.g. the mail link ?kitchen=&item=)
const QmLeitungLayout         = lazy(() => import('./modules/qm/stage/QmFrame'))
const QmUebersichtPage        = lazy(() => import('./modules/qm/QmUebersichtPage'))
const QmAufgabenPage          = lazy(() => import('./modules/qm/QmAufgabenPage'))
const QmNachrichtenPage       = lazy(() => import('./modules/qm/QmNachrichtenPage'))
const QmKalenderPage          = lazy(() => import('./modules/qm/QmKalenderPage'))
const QmChecklistePage        = lazy(() => import('./modules/qm/QmChecklistePage'))
const QmPositionenPage        = lazy(() => import('./modules/qm/QmPositionenPage'))
const QmIntervallPage         = lazy(() => import('./modules/qm/QmIntervallPage'))

// HR · Person-detail page-with-tabs (canonical person view since 2026-05-17)
const HrPersonPage            = lazy(() => import('./modules/hr/person-page/HrPersonPage'))
const HrPersonUebersichtTab   = lazy(() => import('./modules/hr/person-page/tabs/UebersichtTab'))
const HrPersonStammdatenTab   = lazy(() => import('./modules/hr/person-page/tabs/StammdatenTab'))
const HrPersonVertraegeTab    = lazy(() => import('./modules/hr/person-page/tabs/VertraegeTab'))
const HrPersonNotizenTab      = lazy(() => import('./modules/hr/person-page/tabs/NotizenTab'))
const HrPersonDokumenteTab    = lazy(() => import('./modules/hr/person-page/tabs/DokumenteTab'))
const HrPersonBeschaeftigungTab = lazy(() => import('./modules/hr/person-page/tabs/BeschaeftigungTab'))
const HrPersonKrankTab        = lazy(() => import('./modules/hr/person-page/tabs/KrankTab'))
const HrPersonZeiterfassungTab = lazy(() => import('./modules/hr/person-page/tabs/ZeiterfassungTab'))
const HrPersonHistorieTab     = lazy(() => import('./modules/hr/person-page/tabs/HistorieTab'))
const HrPersonUrlaubTab       = lazy(() => import('./modules/hr/person-page/tabs/UrlaubTab'))
const HrPersonBankSteuerTab   = lazy(() => import('./modules/hr/person-page/tabs/BankSteuerTab'))
const HrPersonComplianceTab   = lazy(() => import('./modules/hr/person-page/tabs/ComplianceTab'))
const HrPersonOffboardingTab  = lazy(() => import('./modules/hr/person-page/tabs/OffboardingTab'))
const HrPersonTabPlaceholder  = lazy(() => import('./modules/hr/person-page/tabs/TabPlaceholder'))
// Replace standard schulen dashboard with the restored component
const modules = baseModules.map(mod => {
  if (mod.path === '/schulen') {
    return { ...mod, component: SchulenDashboard }
  }
  return mod
})

// ── Page loading skeleton ─────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="p-8 space-y-4 animate-pulse">
      <div className="h-7 w-48 bg-slate-200 rounded" />
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 bg-slate-200 rounded-xl" />
        ))}
      </div>
      <div className="h-64 bg-slate-200 rounded-xl" />
    </div>
  )
}

// ── M30 QM · /qm-leitung lands on the Übersicht. The query stays: the mail link is /qm-leitung?kitchen=<key>&item=<item_key> ──

function QmLeitungStart() {
  const { search } = useLocation()
  return <Navigate to={`/qm-leitung/uebersicht${search}`} replace />
}

// ── Login landing page ────────────────────────────────────────────────────────

// ── Root layout (persistent across all routes) ────────────────────────────────

function RootLayout() {
  const { isAuthenticated, isLoading } = useAuth()
  const { darkMode } = useUIStore()
  const location = useLocation()

  // Synchronize dark mode class to <html>
  useEffect(() => {
    const root = document.documentElement
    
    // Add dark mode if applicable
    if (darkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [darkMode])


  if (isLoading) {
    // Branded cold start — a bare spinner reads as "broken", logo + spinner as "loading".
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-5">
        <img src="/mammascanteen_logo.svg" alt="Mammas Canteen" className="h-14 w-auto opacity-90" />
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!isAuthenticated) return <LoginPage />

  return (
    <div className="flex h-screen overflow-hidden relative">
      {/* Background scene (refraction source for all Liquid Glass chrome) */}
      <GlassScene />

      {/* Skip to main content — visible on focus only */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-on-primary focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Zum Hauptinhalt springen
      </a>

      <Sidebar />
      <div
        className={`flex flex-col flex-1 min-w-0 w-full lg:ml-[104px]`}
      >
        <Aktivitaetsanzeige />
        <Header />
        {(() => {
          const fullBleed =
            location.pathname.startsWith('/support/megatabelle/v2-mockup') ||
            location.pathname.startsWith('/support/megatabelle/pca') ||
            location.pathname.startsWith('/verwaltung/kostenstellen') ||
            location.pathname.startsWith('/speiseplan')
          // wide: full page width but keep the normal padding (fullBleed drops it)
          const wide = location.pathname.startsWith('/hr/dashboard') || location.pathname.startsWith('/gastromatic')
          // QM Leitung: one stable key and no 0.4 s fade here, so the QM frame (header card, sub-navigation) is not mounted again on a page change;
          // only the page inside leaves (seiteVerlassen) and flies in (.sp-fly), see modules/qm/stage/QmFrame.tsx
          const qmLeitung = location.pathname.startsWith('/qm-leitung')
          return (
            <main
              id="main-content"
              tabIndex={-1}
              className={`flex-1 overflow-x-hidden overflow-y-auto outline-none w-full max-w-[100vw] relative z-[1] ${
                fullBleed ? 'p-0' : 'px-4 sm:px-6 lg:px-10 py-4 sm:py-6'
              }`}
            >
              <div
                key={qmLeitung ? 'qm-leitung' : location.pathname}
                className={`w-full ${qmLeitung ? '' : 'animate-[fade-in-up_0.4s_ease-out_backwards]'} ${
                  fullBleed || wide ? '' : 'mx-auto max-w-7xl'
                }`}
              >
                <Suspense fallback={<PageSkeleton />}>
                  <Outlet />
                </Suspense>
              </div>
            </main>
          )
        })()}
      </div>
      <CommandPalette />
      <BotWidget />
      <ToastContainer />
    </div>
  )
}

// ── Router ────────────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Skip modules flagged noRoute — those are mounted manually below in
      // a nested route group (Kostenstellen layout-rail, Lieferanten-
      // Workbench feature flag, etc.). They still appear in registry.ts so
      // RoleAdmin/permission UIs see them.
      // Index route resolves dynamically via LandingResolver — honours each
      // user's saved `landingModule` preference, falls back to the dashboard
      // when allowed, and to the first accessible module otherwise.
      {
        index: true,
        element: <LandingResolver />,
      },
      // Skip the dashboard's `/` entry; LandingResolver above owns it.
      ...modules
        .filter((mod) => !mod.noRoute && mod.path !== '/')
        .map((mod) => ({
          path: mod.path,
          element: (
            <RoleGuard module={mod.id}>
              <mod.component />
            </RoleGuard>
          ),
        })),
      // ── Kostenstellen nested route group ──
      // KostenstellenPage is the layout (renders SubNav rail + <Outlet />).
      // Children render inside the Outlet so the rail stays mounted across navigation.
      {
        path: '/verwaltung/kostenstellen',
        element: (
          <RoleGuard module="verwaltung-kostenstellen">
            <KostenstellenPage />
          </RoleGuard>
        ),
        children: [
          { index: true, element: <Suspense fallback={<PageSkeleton />}><KostenstellenUebersicht /></Suspense> },
          { path: 'uebersicht',     element: <Suspense fallback={<PageSkeleton />}><KostenstellenUebersicht /></Suspense> },
          { path: 'imports',         element: <Suspense fallback={<PageSkeleton />}><KostenstellenImports /></Suspense> },
          { path: 'imports/ass',     element: <Suspense fallback={<PageSkeleton />}><KostenstellenAssImport /></Suspense> },
          // ChefsCulinar's second surface next to the wizard: what CC pushed to us (Preiskatalog | Rechnungen).
          { path: 'imports/chefsculinar/push', element: <Suspense fallback={<PageSkeleton />}><KostenstellenCcPushLog /></Suspense> },
          // Rosenkrantz vergibt Zugangsdaten je Kundennummer — eigene Verwaltungsfläche.
          { path: 'imports/rosenkrantz/konten', element: <Suspense fallback={<PageSkeleton />}><KostenstellenRkKonten /></Suspense> },
          // Was der Abruf geholt hat — auch der leere Lauf, der sonst nirgends auffällt.
          { path: 'imports/rosenkrantz/abruf', element: <Suspense fallback={<PageSkeleton />}><KostenstellenRkAbruf /></Suspense> },
          // Lieferantenuebergreifend: was die Schwelle nicht durchgelassen hat.
          { path: 'imports/pruefung', element: <Suspense fallback={<PageSkeleton />}><KostenstellenRoutingPruef /></Suspense> },
          { path: 'preiskatalog',    element: <Suspense fallback={<PageSkeleton />}><KostenstellenPreiskatalog /></Suspense> },
          { path: 'bio-quote',       element: <Suspense fallback={<PageSkeleton />}><KostenstellenBioQuote /></Suspense> },
          { path: 'imports/katalog', element: <Suspense fallback={<PageSkeleton />}><KostenstellenKatalogImport /></Suspense> },
          // /imports/pca + /imports/pca-bookings retired 2026-05-26 — both redirect to the Explorer at /pca.
          { path: 'imports/pca',          element: <Navigate to="/verwaltung/kostenstellen/pca" replace /> },
          { path: 'imports/pca-bookings', element: <Navigate to="/verwaltung/kostenstellen/pca" replace /> },
          // /kiosk retired 2026-05-27 — Kiosk data is out of PCA scope; redirect.
          { path: 'kiosk',                element: <Navigate to="/verwaltung/kostenstellen/pca" replace /> },
          { path: 'imports/datev',   element: <Suspense fallback={<PageSkeleton />}><KostenstellenDatevImport /></Suspense> },
          { path: 'imports/refood',  element: <Suspense fallback={<PageSkeleton />}><KostenstellenRefoodImport /></Suspense> },
          // The old winfood-mapping / winfood-zahlungsavis / winfood-reconciliation
          // pages are gone — all three rendered the same avis rows. The one
          // surface is imports/winfood, with ?tab=avis for the ledger.
          { path: 'imports/winfood-reconciliation', element: <Navigate to="/verwaltung/kostenstellen/imports/winfood?tab=avis" replace /> },
          { path: 'imports/winfood', element: <Suspense fallback={<PageSkeleton />}><KostenstellenWinfoodImport /></Suspense> },
          // Brünning Zahlungsavis (article summary per Kunde × Monat, cross-check only) — before imports/:type.
          { path: 'imports/bruening/avis', element: <Suspense fallback={<PageSkeleton />}><KostenstellenBrueningAvis /></Suspense> },
          { path: 'imports/nette', element: <Suspense fallback={<PageSkeleton />}><KostenstellenNetteImport /></Suspense> },
          { path: 'imports/sap-umsaetze', element: <Suspense fallback={<PageSkeleton />}><KostenstellenSapImport /></Suspense> },
          { path: 'imports/:type',   element: <Suspense fallback={<PageSkeleton />}><KostenstellenImportWizard /></Suspense> },
          { path: 'stammdaten',     element: <Suspense fallback={<PageSkeleton />}><KostenstellenStammdaten /></Suspense> },
          { path: 'refood',         element: <Suspense fallback={<PageSkeleton />}><KostenstellenRefood /></Suspense> },
          { path: 'pca',            element: <Suspense fallback={<PageSkeleton />}><KostenstellenPcaExplorer /></Suspense> },
          { path: 'sap-kasse',      element: <Suspense fallback={<PageSkeleton />}><KostenstellenSapKasse /></Suspense> },
          { path: 'auswertung',                       element: <Suspense fallback={<PageSkeleton />}><KostenstellenAuswertung /></Suspense> },
          { path: 'auswertung/schule/:schoolId',       element: <Suspense fallback={<PageSkeleton />}><SchoolDeepDive /></Suspense> },
          { path: 'lieferanten',    element: <Suspense fallback={<PageSkeleton />}><KostenstellenLieferanten /></Suspense> },
        ],
      },
      {
        path: '/verwaltung/lieferanten/kategorisierung/:supplierId',
        element: (
          <RoleGuard module="verwaltung-lieferanten-kategorisierung">
            <Suspense fallback={<PageSkeleton />}><LieferantenKategorisierung /></Suspense>
          </RoleGuard>
        ),
      },
      // ── HR · Onboarding sub-routes (queue page is auto-mounted via registry) ──
      {
        path: '/hr/onboarding/probearbeiter/new',
        element: (
          <RoleGuard module="hr-onboarding-probe-new">
            <Suspense fallback={<PageSkeleton />}><ProbearbeiterFormPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/hr/onboarding/probearbeiter/:id',
        element: (
          <RoleGuard module="hr-onboarding-probe-review">
            <Suspense fallback={<PageSkeleton />}><ProbearbeiterReviewPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/hr/onboarding/wizard/:personMainId',
        element: (
          <RoleGuard module="hr-onboarding-wizard">
            <Suspense fallback={<PageSkeleton />}><OnboardingWizard /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/hr/personen/:mainId/vertrag/:vertragId/contract',
        element: (
          <RoleGuard module="hr-contracts">
            <Suspense fallback={<PageSkeleton />}><ContractPage /></Suspense>
          </RoleGuard>
        ),
      },
      // ── HACCP · detail sub-routes (Leitstand list auto-mounts via registry; these are noRoute) ──
      {
        path: '/verwaltung/haccp/punkt/:pointId',
        element: (
          <RoleGuard module="haccp-geraet">
            <Suspense fallback={<PageSkeleton />}><HaccpGeraetPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/haccp/berichte',
        element: (
          <RoleGuard module="haccp-berichte">
            <Suspense fallback={<PageSkeleton />}><HaccpBerichtePage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/haccp/einstellungen',
        element: (
          <RoleGuard module="haccp-einstellungen">
            <Suspense fallback={<PageSkeleton />}><HaccpEinstellungenPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/haccp/onboarding',
        element: (
          <RoleGuard module="haccp-onboarding">
            <Suspense fallback={<PageSkeleton />}><HaccpOnboardingPage /></Suspense>
          </RoleGuard>
        ),
      },
      // ── M30 QM · Leitung: Übersicht, Kalender, Aufgaben, Nachrichten, and the setup pages Checkliste, Positionen & Küchen, Intervall ──
      { path: '/qm-leitung', element: <QmLeitungStart /> },
      // One layout route: the QM frame (header card, sub-navigation, page leave) stays while the page inside changes.
      {
        element: (
          <RoleGuard module="qm-leitung">
            <Suspense fallback={<PageSkeleton />}><QmLeitungLayout /></Suspense>
          </RoleGuard>
        ),
        children: [
        {
          path: '/qm-leitung/uebersicht',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmUebersichtPage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/kalender',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmKalenderPage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/aufgaben',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmAufgabenPage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/nachrichten',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmNachrichtenPage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/checkliste',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmChecklistePage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/positionen',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmPositionenPage /></Suspense>
            </RoleGuard>
          ),
        },
        {
          path: '/qm-leitung/intervall',
          element: (
            <RoleGuard module="qm-leitung">
              <Suspense fallback={<PageSkeleton />}><QmIntervallPage /></Suspense>
            </RoleGuard>
          ),
        },
        ],
      },
      // ── HR · Person-detail nested route group ──
      // /hr/personen/:mainId/:tabId — HrPersonPage owns layout (sidebar + scroll-
      // linked hero), tabs render inside <Outlet />. Canonical person view since
      // 2026-05-17 (legacy PersonDetailModal + hr_person_page_v1 flag removed).
      {
        path: '/hr/personen/:mainId',
        element: (
          <RoleGuard module="hr-person-detail">
            <Suspense fallback={<PageSkeleton />}><HrPersonPage /></Suspense>
          </RoleGuard>
        ),
        children: [
          { index: true,                element: <Suspense fallback={<PageSkeleton />}><HrPersonUebersichtTab /></Suspense> },
          { path: 'uebersicht',          element: <Suspense fallback={<PageSkeleton />}><HrPersonUebersichtTab /></Suspense> },
          { path: 'stammdaten',          element: <Suspense fallback={<PageSkeleton />}><HrPersonStammdatenTab /></Suspense> },
          { path: 'vertraege',           element: <Suspense fallback={<PageSkeleton />}><HrPersonVertraegeTab /></Suspense> },
          { path: 'notizen',             element: <Suspense fallback={<PageSkeleton />}><HrPersonNotizenTab /></Suspense> },
          { path: 'dokumente',           element: <Suspense fallback={<PageSkeleton />}><HrPersonDokumenteTab /></Suspense> },
          { path: 'beschaeftigung',      element: <Suspense fallback={<PageSkeleton />}><HrPersonBeschaeftigungTab /></Suspense> },
          { path: 'krank',               element: <Suspense fallback={<PageSkeleton />}><HrPersonKrankTab /></Suspense> },
          { path: 'zeiterfassung',       element: <Suspense fallback={<PageSkeleton />}><HrPersonZeiterfassungTab /></Suspense> },
          { path: 'historie',            element: <Suspense fallback={<PageSkeleton />}><HrPersonHistorieTab /></Suspense> },
          { path: 'urlaub',              element: <Suspense fallback={<PageSkeleton />}><HrPersonUrlaubTab /></Suspense> },
          { path: 'bank-steuer',         element: <Suspense fallback={<PageSkeleton />}><HrPersonBankSteuerTab /></Suspense> },
          { path: 'compliance',          element: <Suspense fallback={<PageSkeleton />}><HrPersonComplianceTab /></Suspense> },
          { path: 'offboarding',         element: <Suspense fallback={<PageSkeleton />}><HrPersonOffboardingTab /></Suspense> },
          // Tabs below ship in subsequent waves. They share the shell's permission
          // gating so unauthorised access still 403s cleanly. Unknown/unbuilt
          // tab IDs render TabPlaceholder ("Bald verfügbar") so the sidebar
          // stays honest instead of silently re-rendering Übersicht.
          { path: ':tabId',              element: <Suspense fallback={<PageSkeleton />}><HrPersonTabPlaceholder /></Suspense> },
        ],
      },
      {
        path: '/verwaltung/lieferanten/workbench',
        element: (
          <RoleGuard module="verwaltung-lieferanten-workbench">
            <FeatureFlagGate flag="supplierCategoryMappingV2">
              <Suspense fallback={<PageSkeleton />}><MappingWorkbench /></Suspense>
            </FeatureFlagGate>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/lieferanten/kategorisierung',
        element: (
          <RoleGuard module="verwaltung-lieferanten-kategorisierung">
            <Suspense fallback={<PageSkeleton />}><LieferantenKategorisierung /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/kostenarten',
        element: (
          <RoleGuard module="verwaltung-kostenarten">
            <Suspense fallback={<PageSkeleton />}><KostenartenPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/kasse/kontrolle',
        element: (
          <RoleGuard module="kasse-kontrolle">
            <Suspense fallback={<PageSkeleton />}><KasseKontrolle /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/schulen/kuechen',
        element: (
          <RoleGuard module="schulen-kuechen">
            <Suspense fallback={<PageSkeleton />}><KuechenPage /></Suspense>
          </RoleGuard>
        ),
      },
      {
        path: '/verwaltung/einstellungen/kpis',
        element: (
          <RoleGuard module="verwaltung-einstellungen-kpis">
            <Suspense fallback={<PageSkeleton />}><KpiSettings /></Suspense>
          </RoleGuard>
        ),
      },
      // M26 Mitarbeiterportal — the old single-page route now redirects to
      // its flagship section (Schulungen). registry.ts owns the four real
      // section routes (einstellungen/schulungen/dokumente/mitteilungen).
      { path: '/verwaltung/mitarbeiterportal', element: <Navigate to="/verwaltung/mitarbeiterportal/schulungen" replace /> },
      {
        path: '/access-denied',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <AccessDeniedPage />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
  // M26 Mitarbeiterportal — a SIBLING top-level entry, not a child of
  // RootLayout above: RootLayout gates its whole subtree on the Entra
  // session (`if (!isAuthenticated) return <LoginPage/>`), which
  // kitchen/dishwashing staff never have. StaffApp owns the full
  // /mitarbeiter/* subtree (its own auth, its own nested <Routes>) so it's
  // reachable with zero Entra session. See staff/StaffApp.tsx.
  {
    path: '/mitarbeiter/*',
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <StaffApp />
      </Suspense>
    ),
  },
  // M27 Schulportal (/schule/*) — school card login, own session (mc_schule).
  {
    path: '/schule/*',
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <SchoolApp />
      </Suspense>
    ),
  },
  // M30 QM field app lives in its own entry (/qm/ -> qm.html). Here only a loop-guarded
  // hand-over for /qm and /qm/* when the SPA, not qm.html, answered (S1 a, c).
  { path: '/qm', element: <QmWeiche /> },
  { path: '/qm/*', element: <QmWeiche /> },
])
